import * as React from 'react'
import { useState } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePrice } from '@/lib/api/prices'
import { useProduct, useSearchProducts, SearchProductOutputItem } from '@/lib/api/products'
import { useDropdownPosition } from '@/hooks/use-dropdown-position'
import { Input } from '../../ui/input'
import { ProductDropdown } from './ProductDropdown'
import { VariantDropdown } from './VariantDropdown'
import { PriceDropdown } from './PriceDropdown'
import { IdLookupDropdown } from './IdLookupDropdown'
import { IdResolutionHint } from './IdResolutionHint'
import { describeResolution, parseResourceId, ResolutionSummary } from './resource-id'
import { buildResolutionMessage } from './resolution-message'
import { useResourceIdLookup } from './use-resource-id-lookup'
import { formatPrice } from './utils'
import type { SearchPriceInputProps, SelectionStep, SelectedState } from './types'

const buildDisplayValue = (
    productName: string,
    variantName?: string,
    priceValue?: string,
): string => {
    const parts = [productName]
    if (variantName) parts.push(variantName)
    if (priceValue) parts.push(priceValue)
    return parts.join(' > ')
}

const getPlaceholder = (step: SelectionStep): string => {
    const placeholders = {
        product: 'Search for a product',
        variant: 'Select a variant',
        price: 'Select a price',
    }
    return placeholders[step]
}

const buildInitialState = (price: any, product: any) => {
    if (!price || !product) return null

    const variant = product.variants.find((v: any) => v.id === price.variantId)
    if (!variant) return null

    return {
        searchValue: buildDisplayValue(
            product.name,
            variant.name,
            formatPrice(price.currencyOptions),
        ),
        currentStep: 'price' as SelectionStep,
        selected: {
            product,
            variantId: price.variantId,
            priceId: price.id,
        },
    }
}

export const SearchPriceInput = ({ storeId, priceId, onChange, includeInactive = false, recurringOnly = false }: SearchPriceInputProps) => {
    const { t } = useTranslation('common')
    const [searchValue, setSearchValue] = useState('')
    const [focused, setFocused] = useState(false)
    const [currentStep, setCurrentStep] = useState<SelectionStep>('product')
    const [selected, setSelected] = useState<SelectedState>({})
    const [initialized, setInitialized] = useState(false)
    const [resolution, setResolution] = useState<ResolutionSummary | null>(null)

    const { data: initialPrice, isLoading: isPriceLoading } = usePrice(priceId ?? '', !!priceId)
    const { data: initialProduct, isLoading: isProductLoading } = useProduct(
        initialPrice?.productId ?? '',
        !!initialPrice?.productId,
    )

    // A pasted product / variant / price id resolves directly instead of
    // running a name search that would never match it.
    const idQuery = React.useMemo(() => parseResourceId(searchValue), [searchValue])
    const { status: idStatus, selection: idSelection } = useResourceIdLookup({
        storeId,
        query: idQuery,
        includeInactive,
    })

    const { data: products, isLoading: isSearching } = useSearchProducts(
        {
            storeId,
            productId: null,
            searchValue: currentStep === 'product' ? searchValue : null,
            // Opt-in per call site (default off) — see SearchPriceInputProps.
            includeInactive,
        },
        { enabled: !idQuery },
    )
    // Initialize state when data is loaded
    if (!initialized && initialPrice && initialProduct) {
        const initialState = buildInitialState(initialPrice, initialProduct)
        if (initialState) {
            setSearchValue(initialState.searchValue)
            setCurrentStep(initialState.currentStep)
            setSelected(initialState.selected)
            setInitialized(true)
        }
    }

    // Auto-open dropdown when moving to variant/price steps
    React.useEffect(() => {
        if (priceId) {
            return;
        }
        if (currentStep === 'variant' && selected.product) {
            setFocused(true)
        } else if (currentStep === 'price' && selected.variantId && !selected.priceId) {
            setFocused(true)
        }
    }, [currentStep, selected.product, selected.variantId, selected.priceId, priceId])

    // Apply a resolved id once. Applying rewrites the search box to the display
    // value, so the id stops parsing and the lookup goes idle by itself; the ref
    // only guards against a re-run caused by an unstable `onChange` identity.
    const appliedIdRef = React.useRef<string | null>(null)
    React.useEffect(() => {
        if (!idQuery) {
            appliedIdRef.current = null
            return
        }
        if (!idSelection || appliedIdRef.current === idQuery.id) return
        appliedIdRef.current = idQuery.id

        const { product, variantId, priceId: resolvedPriceId } = idSelection
        const variant = product.variants.find((v) => v.id === variantId)
        const price = variant?.prices.find((p) => p.id === resolvedPriceId)

        setResolution(describeResolution(idQuery, idSelection))
        setSelected({ product, variantId, priceId: resolvedPriceId })
        setSearchValue(
            buildDisplayValue(
                product.name,
                variant?.name,
                price ? formatPrice(price.currencyOptions) : undefined,
            ),
        )

        if (variantId && resolvedPriceId) {
            setCurrentStep('price')
            setFocused(false)
            onChange({ priceId: resolvedPriceId, productId: product.id, variantId })
        } else {
            setCurrentStep(variantId ? 'price' : 'variant')
            setFocused(true)
        }
    }, [idQuery, idSelection, onChange])

    // Any pick of the user's own makes the "what the id filled in" hint stale.
    const handleProductSelect = (product: SearchProductOutputItem) => {
        setResolution(null)
        setSelected({ product })
        setSearchValue(product.name)
        setCurrentStep('variant')
    }

    const handleVariantSelect = (variantId: string) => {
        const variant = selected.product?.variants.find((v) => v.id === variantId)
        if (!variant) return

        setResolution(null)
        setSelected((prev) => ({ ...prev, variantId }))
        setSearchValue(buildDisplayValue(selected.product?.name ?? '', variant.name))
        setCurrentStep('price')
    }

    const handlePriceSelect = (price: any) => {
        if (!selected.product || !selected.variantId) return

        const variant = selected.product.variants.find((v) => v.id === selected.variantId)
        setResolution(null)
        setSelected((prev) => ({ ...prev, priceId: price.id }))
        setSearchValue(
            buildDisplayValue(
                selected.product.name,
                variant?.name,
                formatPrice(price.currencyOptions),
            ),
        )
        setFocused(false)

        onChange({
            priceId: price.id,
            productId: selected.product.id,
            variantId: selected.variantId,
        })
    }

    const handleBack = () => {
        setResolution(null)
        if (currentStep === 'price') {
            setCurrentStep('variant')
            setSelected((prev) => ({ ...prev, priceId: undefined }))
            setSearchValue(selected.product?.name ?? '')
        } else if (currentStep === 'variant') {
            setCurrentStep('product')
            setSelected((prev) => ({ ...prev, variantId: undefined, product: undefined }))
            setSearchValue('')
        }
    }

    const handleFocus = () => {
        setFocused(true)
    }

    const handleBlur = (e: React.FocusEvent) => {
        setFocused(false)
        if (containerRef.current && containerRef.current.contains(e.relatedTarget as Node)) {
            return
        }

    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && (currentStep === 'variant' || currentStep === 'price')) {
            e.preventDefault()
            handleBack()
        }
    }

    // While an id is being resolved it owns the dropdown — the step lists below
    // still describe the previous selection.
    const showIdLookup = focused && !!idQuery && (idStatus === 'loading' || idStatus === 'not-found')
    const showProductDropdown = focused && !idQuery && currentStep === 'product' && (isSearching || (products && products.length > 0))
    const showVariantDropdown = focused && !idQuery && currentStep === 'variant' && selected.product
    const showPriceDropdown = focused && !idQuery && currentStep === 'price' && selected.product && selected.variantId
    const isDropdownOpen = !!(showIdLookup || showProductDropdown || showVariantDropdown || showPriceDropdown)
    const { containerRef, showAbove } = useDropdownPosition(isDropdownOpen)

    // Rides in the open dropdown's header, where the user is already looking;
    // falls back under the input once the selection is complete.
    const resolutionMessage = resolution ? buildResolutionMessage(resolution, t) : null

    const selectedVariant = selected.product?.variants.find((v) => v.id === selected.variantId)
    const allVariantPrices = selectedVariant?.prices ?? []
    const availablePrices = recurringOnly
        ? allVariantPrices.filter((price) => price.recurring)
        : allVariantPrices
    const isLoading = (isPriceLoading || isProductLoading) && !initialized

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
        )
    }

    return (
        <div className="relative isolate" ref={containerRef}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    value={searchValue}
                    onChange={(e) => {
                        setResolution(null)
                        setSearchValue(e.target.value)
                    }}
                    placeholder={
                        currentStep === 'product'
                            ? t('formComponents.searchProductOrId')
                            : getPlaceholder(currentStep)
                    }
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className="pl-10"
                />
            </div>

            {showIdLookup && idQuery && (
                <IdLookupDropdown id={idQuery.id} isLoading={idStatus === 'loading'} showAbove={showAbove} />
            )}

            {showProductDropdown && (
                <ProductDropdown products={products} isLoading={isSearching} onSelect={handleProductSelect} showAbove={showAbove} />
            )}

            {showVariantDropdown && (
                <VariantDropdown variants={selected.product?.variants ?? []} onSelect={handleVariantSelect} onBack={handleBack} showAbove={showAbove} notice={resolutionMessage} />
            )}

            {showPriceDropdown && (
                <PriceDropdown prices={availablePrices} onSelect={handlePriceSelect} onBack={handleBack} showAbove={showAbove} notice={resolutionMessage} />
            )}

            {resolutionMessage && !isDropdownOpen && (
                <IdResolutionHint message={resolutionMessage} className="mt-1.5" />
            )}
        </div>
    )
}

